import { useState } from 'react';
import { line, CHANGE_REQUEST_FIELDS, Color, Size, changeRequestFieldLabel, changeRequestProblem, declineProblem, t, type ChangeRequestStatus, type ChangeRequestView, type DateFormat } from '@bms2/shared';
import { Button, Pill, Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Textarea } from '../../components/controls/Textarea';
import { Modal } from '../../components/modals/Modal';
import { post } from './api';
import { type TileRow } from './tileItems';
import { whenText } from '../../shell/viewerDates';
import styles from './RecordPage.module.css';

/**
 * 🔒 The change-of-data request (piece 21; decision 0971): Request change on an
 * outside login's own company page, and the Change requests tile the account
 * owner decides from. Every control here is drawn from the page answer's own
 * facts and rows, never from a flag the browser holds.
 */
export const CHANGE_REQUESTS_PATH = '/change-requests';

export function changeRequestDecisionPath(id: string, decision: 'accept' | 'decline'): string {
  return `${CHANGE_REQUESTS_PATH}/${id}/${decision}`;
}

const STATUS_TONE: Readonly<Record<ChangeRequestStatus, 'good' | 'warning' | 'inactive'>> = { Open: 'warning', Accepted: 'good', Declined: 'inactive' };
const STATUS_KEY = { Open: 'changeRequest.statusOpen', Accepted: 'changeRequest.statusAccepted', Declined: 'changeRequest.statusDeclined' } as const;

/** The request a tile row carries, read defensively: a row missing a key draws what it has rather than breaking the tile. */
export function changeRequestOf(row: TileRow): ChangeRequestView | null {
  const raw = row.raw ?? {};
  const text = (key: string) => (typeof raw[key] === 'string' ? (raw[key] as string) : null);
  const status = text('status');
  if (!row.id || (status !== 'Open' && status !== 'Accepted' && status !== 'Declined')) return null;
  return {
    id: row.id,
    field: text('field'),
    fieldLabel: text('fieldLabel'),
    requestText: text('requestText') ?? row.label,
    status,
    requestedBy: text('requestedBy') ?? '',
    createdAt: text('createdAt') ?? '',
    decidedBy: text('decidedBy'),
    decidedAt: text('decidedAt'),
    decisionNote: text('decisionNote'),
    decidable: raw.decidable === true,
  };
}

/** Request change: the button on the banner row and the window it opens (decision 0971). */
export function RequestChangeButton({ companyId, apiBaseUrl = '', onFiled }: { companyId: string; apiBaseUrl?: string; onFiled: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span data-record-action="request-change">
      <Button size={Size.Small} color={Color.Navy} text={[t('changeRequest.action')]} actions={[() => setOpen(true)]} />
      {open ? <RequestChangeModal companyId={companyId} apiBaseUrl={apiBaseUrl} onCancel={() => setOpen(false)} onFiled={() => { setOpen(false); onFiled(); }} /> : null}
    </span>
  );
}

/** The window: the request in free text, and the field of the company it concerns where one is named. */
export function RequestChangeModal({ companyId, apiBaseUrl = '', onCancel, onFiled }: { companyId: string; apiBaseUrl?: string; onCancel: () => void; onFiled: () => void }) {
  const [field, setField] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    // 🔨 The window refuses what the route refuses, in the same words, from the one rule (decision 0971).
    const problem = changeRequestProblem({ requestText: text, field });
    if (problem) return setError(problem);
    setBusy(true);
    const answer = await post<{ id: string }>(CHANGE_REQUESTS_PATH, { companyId, field: field || null, requestText: text.trim() }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onFiled();
  };
  return (
    <Modal open title={t('changeRequest.title')} onCancel={onCancel} dataModal="request-change" actions={[{ kind: 'primary', label: t('changeRequest.send'), onAct: () => void send(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={styles.hint}>{t('changeRequest.hint')}</p>
      <Select size={Size.Large} label={t('changeRequest.field')} value={field} onChange={setField} placeholder={t('changeRequest.anyField')} options={CHANGE_REQUEST_FIELDS.map((key) => ({ value: key, label: changeRequestFieldLabel(key) ?? key }))} dataAttribute={{ 'data-change-request-field': 'true' }} />
      <Textarea size={Size.Large} label={t('changeRequest.text')} value={text} onChange={setText} required rows={4} name="requestText" />
    </Modal>
  );
}

export interface ChangeRequestsPanelProps {
  rows: readonly TileRow[];
  zone: string;
  dateFormat: DateFormat;
  apiBaseUrl?: string;
  /** Accept opens the company's edit window on the request; the page holds that window (decision 0971). */
  onAccept: (request: ChangeRequestView) => void;
  onChanged: () => void;
}

/**
 * 🔒 The Company page's Change requests tile (piece 13; decision 0971): each
 * request with who filed it and when, its status and any decision note, and
 * Accept and Decline where the row says this viewer decides it now.
 */
export function ChangeRequestsPanel({ rows, zone, dateFormat, apiBaseUrl = '', onAccept, onChanged }: ChangeRequestsPanelProps) {
  const [declining, setDeclining] = useState<ChangeRequestView | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const requests = rows.map(changeRequestOf).filter((row): row is ChangeRequestView => row !== null);
  return (
    <div className={styles.verify} data-change-requests={String(requests.length)}>
      {message ? <AlertBanner tone="inactive" message={message} /> : null}
      {requests.length === 0 ? <p className={styles.changeRequestsEmpty} data-change-requests-empty="true">{t('changeRequest.none')}</p> : null}
      {requests.map((request) => (
        <div key={request.id} className={styles.verify} data-change-request={request.id} data-change-request-status={request.status}>
          <div className={styles.actions}>
            <Pill text={t(STATUS_KEY[request.status])} foreground={`var(--status-${STATUS_TONE[request.status]}-text)`} background={`var(--status-${STATUS_TONE[request.status]}-bg)`} size={Size.Small} />
            {request.fieldLabel ? <span className={styles.verifyTitle}>{request.fieldLabel}</span> : null}
          </div>
          <span>{request.requestText}</span>
          <span className={styles.hint}>{line(t('changeRequest.filedBy'), request.requestedBy, '·', whenText(request.createdAt, zone, dateFormat))}</span>
          {request.decidedAt ? <span className={styles.hint}>{line(t('changeRequest.decidedBy'), request.decidedBy ?? '', '·', whenText(request.decidedAt, zone, dateFormat))}</span> : null}
          {request.decisionNote ? <span data-change-request-note="true">{request.decisionNote}</span> : null}
          {request.decidable ? (
            <div className={styles.actions}>
              <Button size={Size.Small} color={Color.Navy} text={[t('changeRequest.accept')]} actions={[() => onAccept(request)]} />
              <Button size={Size.Small} color={Color.Inactive} text={[t('changeRequest.decline')]} actions={[() => setDeclining(request)]} />
            </div>
          ) : null}
        </div>
      ))}
      {declining ? (
        <DeclineChangeRequestModal
          key={`decline-${declining.id}`}
          request={declining}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setDeclining(null)}
          onDeclined={() => { setDeclining(null); setMessage(t('changeRequest.declined')); onChanged(); }}
        />
      ) : null}
    </div>
  );
}

/** Decline, with the note the requester reads; the note is required (decision 0971). */
export function DeclineChangeRequestModal({ request, apiBaseUrl = '', onCancel, onDeclined }: { request: ChangeRequestView; apiBaseUrl?: string; onCancel: () => void; onDeclined: () => void }) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const decline = async () => {
    const problem = declineProblem(note);
    if (problem) return setError(problem);
    setBusy(true);
    const answer = await post(changeRequestDecisionPath(request.id, 'decline'), { note: note.trim() }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onDeclined();
  };
  return (
    <Modal open title={t('changeRequest.declineTitle')} onCancel={onCancel} dataModal="decline-change-request" actions={[{ kind: 'primary', label: t('changeRequest.decline'), onAct: () => void decline(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p>{request.requestText}</p>
      <p className={styles.hint}>{t('changeRequest.declineHint')}</p>
      <Textarea size={Size.Large} label={t('changeRequest.declineNote')} value={note} onChange={setNote} required rows={3} name="decisionNote" />
    </Modal>
  );
}

/**
 * What Accept opens the edit window with: the named field holding the request's words, and the request above the form.
 * 🔒 A decider the page says writes no Company (the account owner by that rule alone) is offered the fields a request
 * may name and nothing else, the same keys the Accept route saves for them (T355).
 */
export function acceptRequestOf(request: ChangeRequestView, editsRecord = true): { id: string; prefill: Record<string, unknown>; notice: string; fields: readonly string[] | null } {
  return {
    id: request.id,
    fields: editsRecord ? null : CHANGE_REQUEST_FIELDS,
    prefill: request.field ? { [request.field]: request.requestText } : {},
    notice: line(t('changeRequest.acceptNotice'), request.requestText, request.fieldLabel ? line('·', t('changeRequest.acceptField'), request.fieldLabel) : null),
  };
}
